/**
 * 날짜 유틸리티 함수
 * 서버에서 UTC+0으로 저장된 시간을 UTC+9 (한국 시간)로 변환
 * Windows 환경에서도 작동하도록 명시적으로 9시간을 더함
 */

/**
 * Date 객체를 KST 시간대로 포맷팅하는 헬퍼 함수
 * @param date Date 객체 (UTC 시간)
 * @returns KST 시간의 포맷팅된 값들
 */
const getKSTComponents = (date: Date) => {
  // UTC 시간에 9시간(32400000ms)을 더한 밀리초 계산
  const utcTime = date.getTime();
  const kstTime = utcTime + (9 * 60 * 60 * 1000); // UTC+9 (KST)
  
  // kstTime을 UTC 기준으로 해석한 Date 객체 생성
  // 이렇게 하면 브라우저의 로컬 타임존 설정에 영향받지 않음
  const kstDate = new Date(kstTime);
  
  // UTC 메서드를 사용하여 KST 시간의 각 구성 요소를 가져옴
  // kstTime은 이미 9시간이 더해진 값이므로, getUTCHours()는 실제 KST 시간을 나타냄
  return {
    year: kstDate.getUTCFullYear(),
    month: kstDate.getUTCMonth() + 1, // 월은 0부터 시작하므로 +1
    day: kstDate.getUTCDate(),
    hour: kstDate.getUTCHours(),
    minute: kstDate.getUTCMinutes(),
    second: kstDate.getUTCSeconds(),
  };
};

/**
 * 날짜를 한국 시간(UTC+9)으로 포맷팅
 * @param dateString ISO 8601 형식의 날짜 문자열 (UTC)
 * @returns 한국 시간으로 변환된 날짜 문자열 (YYYY-MM-DD)
 */
export const formatDateToKST = (dateString: string): string => {
  if (!dateString) return '-';
  try {
    // ISO 8601 형식이 'Z'로 끝나지 않으면 'Z'를 추가하여 UTC로 명시
    const normalizedDateString = dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
      ? dateString
      : `${dateString}Z`;
    const date = new Date(normalizedDateString);
    
    // Date 객체가 유효한지 확인
    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return '-';
    }
    
    const components = getKSTComponents(date);
    
    // Windows 환경 호환성을 위해 직접 포맷팅
    const year = components.year;
    const month = String(components.month).padStart(2, '0');
    const day = String(components.day).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('formatDateToKST error:', error);
    return '-';
  }
};

/**
 * 날짜와 시간을 한국 시간(UTC+9)으로 포맷팅
 * @param dateString ISO 8601 형식의 날짜 문자열 (UTC)
 * @returns 한국 시간으로 변환된 날짜/시간 문자열
 */
export const formatDateTimeToKST = (dateString: string): string => {
  if (!dateString) return '-';
  try {
    // ISO 8601 형식이 'Z'로 끝나지 않으면 'Z'를 추가하여 UTC로 명시
    const normalizedDateString = dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
      ? dateString
      : `${dateString}Z`;
    const date = new Date(normalizedDateString);
    
    // Date 객체가 유효한지 확인
    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return '-';
    }
    
    const components = getKSTComponents(date);
    
    // Windows 환경 호환성을 위해 직접 포맷팅
    const year = components.year;
    const month = String(components.month).padStart(2, '0');
    const day = String(components.day).padStart(2, '0');
    const hour = String(components.hour).padStart(2, '0');
    const minute = String(components.minute).padStart(2, '0');
    const second = String(components.second).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  } catch (error) {
    console.error('formatDateTimeToKST error:', error);
    return '-';
  }
};

/**
 * 날짜와 시간을 분리하여 반환 (한국 시간)
 * @param dateString ISO 8601 형식의 날짜 문자열 (UTC)
 * @returns { dateStr: 날짜 문자열, timeStr: 시간 문자열 }
 */
export const formatDateAndTimeToKST = (dateString: string): { dateStr: string; timeStr: string } => {
  if (!dateString) return { dateStr: '-', timeStr: '-' };
  try {
    // ISO 8601 형식이 'Z'로 끝나지 않으면 'Z'를 추가하여 UTC로 명시
    const normalizedDateString = dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
      ? dateString
      : `${dateString}Z`;
    const date = new Date(normalizedDateString);
    
    // Date 객체가 유효한지 확인
    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return { dateStr: '-', timeStr: '-' };
    }
    
    const components = getKSTComponents(date);
    
    // Windows 환경 호환성을 위해 직접 포맷팅
    const year = components.year;
    const month = String(components.month).padStart(2, '0');
    const day = String(components.day).padStart(2, '0');
    const hour = String(components.hour).padStart(2, '0');
    const minute = String(components.minute).padStart(2, '0');
    const second = String(components.second).padStart(2, '0');
    
    return {
      dateStr: `${year}-${month}-${day}`,
      timeStr: `${hour}:${minute}:${second}`,
    };
  } catch (error) {
    console.error('formatDateAndTimeToKST error:', error);
    return { dateStr: '-', timeStr: '-' };
  }
};

/**
 * 기본 날짜 범위를 반환 (한 달 전 ~ 오늘)
 * 빌링, 크레딧, 사용량 등 기간 필터의 기본값으로 사용
 * @returns { startDate: string, endDate: string } YYYY-MM-DD 형식
 */
export const getDefaultDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const formatDateLocal = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  return {
    startDate: formatDateLocal(oneMonthAgo),
    endDate: formatDateLocal(today),
  };
};

/**
 * 날짜만 포맷팅 (간단한 버전)
 * formatDateToKST의 래퍼로, undefined/null을 처리
 * @param dateString ISO 8601 형식의 날짜 문자열 (UTC) 또는 Date 객체 또는 undefined/null
 * @returns 한국 시간으로 변환된 날짜 문자열
 */
export const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return '-';
  // Date 객체인 경우 ISO 문자열로 변환
  if (dateString instanceof Date) {
    return formatDateToKST(dateString.toISOString());
  }
  return formatDateToKST(dateString);
};
